import {
    ActionImpactData,
    ActionPlanAction,
    ImpactData,
    PropertyId,
    TargetSsqProperty,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { getSubpropertiesFromPropertyTree } from "../formatMultivalue/getSubpropertiesFromPropertyTree";

type Options = {
    actionPlanAction: ActionPlanAction | undefined;
    impactData: ImpactData;
    selectedScoreId: number;
    targetProperties: TargetSsqProperty[];
};
export const formatComparisonTable = (options: Options): MultiImpactTable | null => {
    const { actionPlanAction, impactData, selectedScoreId, targetProperties } = options;

    const emptyImpact = Object.keys(impactData).length === 0;
    if (actionPlanAction === undefined || emptyImpact) {
        return null;
    }

    let allActions = new Set<number>();
    const propertiesToKeep = targetProperties.map((v) => v.id);

    const subproperties = getSubpropertiesFromPropertyTree(actionPlanAction);
    const allPropertyIds = subproperties.properties.reduce((acc, i) => acc.add(i.id), new Set<number>());
    const _allExistingPositions = actionPlanAction.propertiesTreeElements.reduce((acc, elem) => {
        if (allPropertyIds.has(elem.ssq_property_id)) {
            return acc.add(elem.position);
        }
        return acc;
    }, new Set<number>());
    const allExistingPositions = [..._allExistingPositions.keys()];

    const multivalueImpactData = allExistingPositions.flatMap((position) => {
        const actionsImpact = new Set<number>();

        const propertiesImpactData = Object.entries(impactData).reduce<Record<PropertyId, ActionImpactData[]>>(
            (acc, [propertyIdString, impactDataPerPosition]) => {
                if (impactDataPerPosition === undefined) {
                    return acc;
                }

                const propertyId = parseInt(propertyIdString);

                if (propertiesToKeep.includes(propertyId) === false) {
                    return acc;
                }
                // Handle both array and object formats
                const impactDataAtPosition = impactDataPerPosition[position.toString()] ?? [];

                if (!acc[propertyId]) {
                    acc[propertyId] = [];
                }

                acc[propertyId] = [...acc[propertyId], ...impactDataAtPosition];

                return acc;
            },
            {},
        );

        const propertiesIds = Object.keys(propertiesImpactData);
        const properties = targetProperties.filter((property) => propertiesIds.includes(property.id.toString()));

        if (properties.length === 0) return [];
        const scoreId = selectedScoreId;

        const propertiesWithComposedImpacts = properties.filter(
            (property) => propertiesImpactData[property.id].length > 1,
        );
        const propertiesWithComposedImpactsNames = propertiesWithComposedImpacts.map(
            (property) => property.name || property.title,
        );
        // .join(", ");

        let numberOfLines = 0;
        const summedActionsImpact = Object.entries(propertiesImpactData).reduce(
            (acc: number, [propertyId, multivaluedImpacts]) => {
                // Skip not relevant data
                if (propertiesToKeep.includes(Number(propertyId)) === false) return acc;

                const propertyImpact = multivaluedImpacts.reduce((acc, impactData) => {
                    // Skip bad data
                    numberOfLines += 1;
                    actionsImpact.add(impactData.actionId);
                    const scoreToAdd = impactData.impactScores[scoreId];
                    if (scoreToAdd === undefined) {
                        return acc;
                    }
                    return acc + scoreToAdd;
                }, 0);

                if (propertyImpact === undefined) return acc;

                return acc + propertyImpact;
            },
            0,
        );

        const formattedSummedActionsImpact = summedActionsImpact;
        const shouldDisplaySummedImpact = numberOfLines > 1;
        const hasMultipleActions = actionsImpact.size > 1;
        allActions = allActions.union(actionsImpact);

        // Bad to take 1st from properties
        // Here I have a wrong multi value
        // Because it takes the 1st action field targetSsqProperties PERIOD
        const multivalueParent = properties[0].parent;
        const propertyParentId = multivalueParent?.id;
        const propertiesTree = actionPlanAction.action.propertiesTree;
        const propertyTreeSubproperty = propertiesTree?.[propertyParentId ?? 0];
        const firstSubpropertyToShowId = propertyTreeSubproperty?.[0];

        const subpropertyElement = actionPlanAction.propertiesTreeElements.find(
            (subproperty) =>
                subproperty.position === position && subproperty.ssq_property_id === firstSubpropertyToShowId,
        );
        const subpropertyValue = subpropertyElement?.value;

        const extendedImpactData: ExtendedImpactData[] = Object.entries(propertiesImpactData).flatMap(
            ([propertyId, items]) => {
                const property = targetProperties.find((i) => i.id === Number(propertyId));
                if (property === undefined) {
                    return [];
                }

                const extendedItems = items.flatMap((item) => {
                    const scoreItem = item.impactScores[scoreId];
                    if (scoreItem === undefined) {
                        return [];
                    }

                    return [
                        {
                            propertyId: property.id,
                            propertyLabel: property.name || property.title,
                            ...item,
                        },
                    ];
                });

                return extendedItems;
            },
        );

        const noRows = extendedImpactData.length === 0;
        if (noRows) {
            // Don't even show the section.
            return [];
        }

        return [
            {
                position,
                subpropertyValue,
                formattedSummedActionsImpact,
                shouldDisplaySummedImpact,
                propertiesWithComposedImpactsNames,
                multivalueParent,
                propertiesWithComposedImpacts,
                extendedImpactData,
                targetProperties: properties,
                hasMultipleActions,
            },
        ];
    });

    return {
        allActions,
        tableData: multivalueImpactData,
    };
};

export type ExtendedImpactData = ActionImpactData & {
    propertyId: number;
    propertyLabel: string;
};
export type MultiImpactTableData = {
    position: number;
    subpropertyValue: string | undefined;
    formattedSummedActionsImpact: number | null;
    shouldDisplaySummedImpact: boolean;
    hasMultipleActions: boolean;
    propertiesWithComposedImpactsNames: string[];
    multivalueParent:
        | {
              id: number;
              name?: string | undefined;
              title: string;
          }
        | undefined;
    propertiesWithComposedImpacts: TargetSsqProperty[];
    // propertiesImpactData: Record<string, ExtendedImpactData[]>;
    extendedImpactData: ExtendedImpactData[];
    targetProperties: TargetSsqProperty[];
};
export type MultiImpactTable = {
    allActions: Set<number>;
    tableData: MultiImpactTableData[];
};
